/**
 * StartConsultationPage
 * ---------------------
 * The entry point for an online consultation, reached from the raised centre
 * button in the quick bar.
 *
 * There is no service to pick here. The client captures first, sees what was
 * detected, and only then chooses which analysis they want — so this screen
 * states the promise and gets out of the way with a single action. It resumes
 * an unpaid consultation rather than silently stacking a second one.
 *
 * Nothing here charges anything: `startConsultation` issues a capture invite
 * and the client pays later, once they have usable photos and a doctor.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Container from '@/components/PageSheet';
import { consultationsApi } from '@/services/api';
import { localized } from '@/utils/localizedContent';

const RESUMABLE = ['draft_capture', 'awaiting_purchase'];

export default function StartConsultationPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const termsQuery = useQuery({
    queryKey: ['consultation-terms'],
    queryFn: () => consultationsApi.getConsultationTerms(),
    staleTime: 5 * 60 * 1000,
  });

  const consultationsQuery = useQuery({
    queryKey: ['my-consultations'],
    queryFn: () => consultationsApi.listConsultations(),
  });

  const resumable = (consultationsQuery.data || []).find((c) => RESUMABLE.includes(c.status));
  const terms = termsQuery.data || {};

  const startMut = useMutation({
    mutationFn: () => consultationsApi.startConsultation(),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['my-consultations'] });
      navigate(`/face-capture?consultation=${encodeURIComponent(c.id)}`);
    },
    onError: (err) =>
      setError(err?.response?.data?.error?.message || t('common.error')),
  });


  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={800}>
        {t('startConsultation.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('startConsultation.subtitle')}
      </Typography>

      {/* The promise, before any price is shown. */}
      <Card
        elevation={0}
        sx={{ mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
        <CardContent>
          <Stack spacing={1.25}>
            <Promise
              icon={<PhotoCameraIcon fontSize="small" />}
              text={t('startConsultation.promiseFree')}
            />
            <Promise
              icon={<VerifiedIcon fontSize="small" />}
              text={t('startConsultation.promiseSla', { hours: terms.slaHours ?? 48 })}
            />
            <Promise
              icon={<ChatBubbleOutlineIcon fontSize="small" />}
              text={t('startConsultation.promiseChat', { days: terms.chatDays ?? 7 })}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* An unpaid consultation is resumed, never duplicated. */}
      {resumable && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 3 }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() =>
                navigate(
                  resumable.status === 'draft_capture'
                    ? `/face-capture?consultation=${encodeURIComponent(resumable.id)}`
                    : `/consultations/${encodeURIComponent(resumable.id)}`,
                )
              }
            >
              {t('startConsultation.resumeCta')}
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 800 }}>{t('startConsultation.resumeTitle')}</AlertTitle>
          {t('startConsultation.resumeBody', {
            service: localized(resumable.serviceName, lang) || '',
          })}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      <Button
        fullWidth
        size="large"
        variant="contained"
        startIcon={<PhotoCameraIcon />}
        disabled={startMut.isPending}
        onClick={() => startMut.mutate()}
      >
        {startMut.isPending ? t('common.loading') : t('startConsultation.cta')}
      </Button>
      <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 1 }}>
        {t('startConsultation.ctaHint')}
      </Typography>

    </Container>
  );
}

function Promise({ icon, text }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{ color: 'secondary.main', mt: '2px', display: 'flex' }}>{icon}</Box>
      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
        {text}
      </Typography>
    </Stack>
  );
}
